'use client';

import React, { useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/navigation';
import { useSwipeScroll } from '@/shared/hooks/dom/scroll/useSwipeScroll';
import { classNames } from '@/shared/lib/classnames/classnames';
import { Button } from '@/shared/ui/button';
import { Sheet, SheetTrigger } from '@/shared/ui/sheet';
import { getMenuItems } from '../../utils/getMenuItems';
import { MobileLanguageSelector } from './MobileLanguageSelector';
import baseIcons from '@/shared//assets/sprites/base.sprite.svg';
import btnIcons from '@/shared/assets/sprites/btns.sprite.svg';
interface Props {
  cityAlias: string;
}
export const HeaderMenuButton: React.FC<Props> = ({
  cityAlias
}) => {
  const t = useTranslations();
  const menuItems = getMenuItems(cityAlias);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);
  const swipeHandlers = useSwipeScroll({
    onSwipeRight: () => setIsMenuOpen(false),
    threshold: 45
  });
  return <>
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen} data-sentry-element="Sheet" data-sentry-source-file="HeaderMenuButton.tsx">
        <SheetTrigger asChild className="lg:hidden" data-sentry-element="SheetTrigger" data-sentry-source-file="HeaderMenuButton.tsx">
          <Button variant="circle" onClick={toggleMenu} aria-label={t('shared.aria.navbar_menu')} className="p-0" data-sentry-element="Button" data-sentry-source-file="HeaderMenuButton.tsx">
            <svg className="size-8 stroke-textPrimary" aria-hidden="true" data-sentry-element="svg" data-sentry-source-file="HeaderMenuButton.tsx">
              <use xlinkHref={`${baseIcons}#menu-open`} data-sentry-element="use" data-sentry-source-file="HeaderMenuButton.tsx" />
            </svg>
          </Button>
        </SheetTrigger>
        <div role="dialog" aria-modal="true" aria-label={t('shared.aria.navbar')} className={classNames('fixed inset-y-0 right-0 z-50 w-80 bg-white transition-transform duration-300 ease-in-out lg:hidden', {
        'translate-x-0': isMenuOpen,
        'translate-x-full': !isMenuOpen
      })} {...swipeHandlers}>
          <div className="flex items-center justify-between border-b p-4">
            <MobileLanguageSelector data-sentry-element="MobileLanguageSelector" data-sentry-source-file="HeaderMenuButton.tsx" />
            <Button variant="circle" onClick={toggleMenu} aria-label={t('shared.aria.navbar_close')} className="p-0" data-sentry-element="Button" data-sentry-source-file="HeaderMenuButton.tsx">
              <svg className="size-8 stroke-textPrimary" aria-hidden="true" data-sentry-element="svg" data-sentry-source-file="HeaderMenuButton.tsx">
                <use xlinkHref={`${baseIcons}#menu-close`} data-sentry-element="use" data-sentry-source-file="HeaderMenuButton.tsx" />
              </svg>
            </Button>
          </div>
          <nav aria-label={t('shared.aria.main_navigation')}>
            <ul className="divide-y divide-gray-100" role="menu">
              {menuItems.map((item, index) => <li key={`menu-items-${index}`} role="none">
                  <Link href={item.href} prefetch={false} className="flex w-full items-center justify-between px-4 py-3 hover:bg-gray-100" role="menuitem">
                    {t(item.name)}
                    <span className="float-right" aria-hidden="true">
                      <svg className="h-2.5 w-1.5 stroke-textPrimary">
                        <use xlinkHref={`${btnIcons}#arrow`} />
                      </svg>
                    </span>
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>
        {isMenuOpen && <div role="presentation" className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={toggleMenu} />}
      </Sheet>
    </>;
};